import { Dispatch } from 'redux';
import { RegionDto, UrlKeyDto, UserRegionDto } from '@trucktrax/trucktrax-ts-common';
import { getRequest } from './requestService';
import { devErrorAndLog } from '../util/errorUtil';
import {
  ERROR_TEXT_FETCH_ASSIGNED_REGION_LIST,
  ERROR_TEXT_FETCH_REGION_LIST,
} from '../constants/errorConstants';
import {
  EMPTY_ALL_FIELDS,
  SET_ASSIGNED_REGION_LIST,
  SET_CURRENT_REGION,
  SET_DEACTIVATED_REGION_LIST,
  SET_REGION_LIST,
  UPDATE_REGION,
} from '../constants/actionConstants';
import { REGIONS_PATH } from '../constants/apiConstants';
import { reauthorize } from './authService';
import { setUserPermissionObject, initializeNewRegion } from './permissionsService';
import { getGeotraxBaseUrl } from '../util/apiUtil';
import { AssignedRegionDto, UserPermissionsByRegion } from '../types';
import { getIdFromUrl, hasItems, isEmptyArray, isNotEmptyString } from '../util/appUtil';
import { getUserRegionUrls } from '../util/authUtil';
import { INVALID_STALE_TOKEN_LOGOUT } from '../constants/appConstants';
import HTTP_CODES from '../constants/httpConstants';

type GetStore = () => {
  assignedRegionList: AssignedRegionDto[];
  currentRegion: UrlKeyDto;
  defaultRegionList: UserRegionDto[];
  currentSubscription: UrlKeyDto;
};

type GetState = () => {
  userPermission: UserPermissionsByRegion;
  currentRegion: UrlKeyDto;
  assignedRegionList: AssignedRegionDto[];
};

interface AssignedRegionList {
  data: RegionDto[];
}

export function setCurrentRegion(value: UrlKeyDto | string) {
  return (dispatch: Dispatch) => {
    dispatch({ type: EMPTY_ALL_FIELDS });
    dispatch({
      type: SET_CURRENT_REGION,
      payload: value,
    });
  };
}

export async function getRegionByUrl(url?: string) {
  if (!url) {
    return undefined;
  }

  const id = getIdFromUrl(url);
  const backendUrl = `${getGeotraxBaseUrl()}${REGIONS_PATH}/${id}`;

  const result = await getRequest(backendUrl);
  return result?.data ?? null;
}

export async function getRegionById(id?: string | number) {
  if (!id) {
    return undefined;
  }
  const backendUrl = `${getGeotraxBaseUrl()}${REGIONS_PATH}/${id}`;

  const result = await getRequest(backendUrl);
  return result?.data ?? null;
}

// The persisted list stays the fallback because an empty destination makes the server substitute
// the token's default region.
function assignedRegionUrls(persisted: AssignedRegionDto[]): string[] {
  const userRegionUrls = getUserRegionUrls();
  if (hasItems(userRegionUrls)) {
    return userRegionUrls;
  }

  return persisted.map(region => region.url).filter(isNotEmptyString);
}

async function requestAssignedRegions(
  dispatch: Dispatch<any>,
  getStore: GetStore,
): Promise<AssignedRegionDto[]> {
  const { assignedRegionList } = getStore();
  const url = `${getGeotraxBaseUrl() + REGIONS_PATH}/assignment`;
  const destination = assignedRegionUrls(assignedRegionList ?? []).join(',');

  const response: AssignedRegionList = await getRequest(url, { destination });
  const regions = response.data ?? [];

  dispatch({
    type: SET_ASSIGNED_REGION_LIST,
    payload: regions,
  });

  return regions;
}

export function fetchAssignedRegionList(
  socketInitCallback: (region: UrlKeyDto, subscription: UrlKeyDto) => void,
  getMapviewsCallback: (regionUrl: string) => void,
) {
  return async (dispatch: Dispatch<any>, getStore: GetStore): Promise<boolean> => {
    const { defaultRegionList, currentSubscription } = getStore();

    try {
      const regions = await requestAssignedRegions(dispatch, getStore);

      if (isEmptyArray(regions)) {
        return true;
      }

      const { currentRegion } = getStore();

      // set first region as default,
      const preferredDefaultRegion = isEmptyArray(defaultRegionList)
        ? []
        : regions.filter(region => region.url === defaultRegionList[0].url);

      const defaultRegion = isEmptyArray(preferredDefaultRegion)
        ? regions[0]
        : preferredDefaultRegion[0];

      const currentRegionIsActive =
        isNotEmptyString(currentRegion?.url) &&
        regions.some(region => region.url === currentRegion.url);
      if (currentRegionIsActive) {
        socketInitCallback(currentRegion, getStore().currentSubscription);
        getMapviewsCallback(currentRegion.url);
        return true;
      }

      // if there is no current region selected or previously saved in local storage,
      // the app defaults to defaultRegion services
      socketInitCallback(defaultRegion, currentSubscription);
      getMapviewsCallback(defaultRegion.url!);
      dispatch({
        type: SET_CURRENT_REGION,
        payload: defaultRegion,
      });
      return true;
    } catch (e: any) {
      if (e !== INVALID_STALE_TOKEN_LOGOUT) {
        const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
        dispatch(
          devErrorAndLog(
            e.toString(),
            ERROR_TEXT_FETCH_ASSIGNED_REGION_LIST,
            e,
            undefined,
            consoleOnly,
          ) as any,
        );
      }
      return false;
    }
  };
}

export function handleRegionAdded(region: RegionDto) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const newUserPermissionDictionary = await initializeNewRegion(region);
    // get a new token with assigned_regions that include this new one.
    await dispatch(reauthorize());
    const { assignedRegionList } = getState();

    dispatch(setUserPermissionObject(newUserPermissionDictionary));

    // update assigned regions
    const allAssignedRegions = [...assignedRegionList, region];
    dispatch({
      type: SET_ASSIGNED_REGION_LIST,
      payload: allAssignedRegions,
    });

    // have new region active
    dispatch(setCurrentRegion(region));
  };
}

export function handleRegionArchivedChange(region: RegionDto) {
  return async (dispatch: Dispatch<any>, getStore: GetStore) => {
    dispatch({ type: UPDATE_REGION, payload: region });

    try {
      await requestAssignedRegions(dispatch, getStore);
    } catch (e: any) {
      dispatch(
        devErrorAndLog(
          e.toString(),
          ERROR_TEXT_FETCH_ASSIGNED_REGION_LIST,
          e,
          undefined,
          true,
        ) as any,
      );
    }
  };
}

function fetchRegions(
  isArchived: boolean,
  type: typeof SET_REGION_LIST | typeof SET_DEACTIVATED_REGION_LIST,
) {
  return async (dispatch: Dispatch<any>) => {
    const url = getGeotraxBaseUrl() + REGIONS_PATH;
    try {
      const response = await getRequest(url, { isDeleted: false, isArchived });
      const regions: RegionDto[] = response.data.items ?? [];
      dispatch({ type, payload: regions });
    } catch (e: any) {
      if (e !== INVALID_STALE_TOKEN_LOGOUT) {
        const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
        dispatch(
          devErrorAndLog(
            e.toString(),
            ERROR_TEXT_FETCH_REGION_LIST,
            e,
            undefined,
            consoleOnly,
          ) as any,
        );
      }
    }
  };
}

export function fetchRegionList() {
  return fetchRegions(false, SET_REGION_LIST);
}

export function fetchDeactivatedRegionList() {
  return fetchRegions(true, SET_DEACTIVATED_REGION_LIST);
}
